import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@amzn/awsui-components-react/polaris/table';
import Box from '@amzn/awsui-components-react/polaris/box';
import Button from '@amzn/awsui-components-react/polaris/button';
import Header from '@amzn/awsui-components-react/polaris/header';
import Link from '@amzn/awsui-components-react/polaris/link';
import Pagination from '@amzn/awsui-components-react/polaris/pagination';
import { PropertyFilterProps } from '@amzn/awsui-components-react/polaris/property-filter';
import { Person } from '@amzn/red-velvet-api';
import { useTableStrings } from '../../hooks/localization/tableStrings';
import { usePaginationStrings } from '../../hooks/localization/paginationStrings';
import { PeoplePropertyFilter } from '../attendance/csvAttendance/peoplePropertyFilter';
import { PeoplePreferences } from '../attendance/wizard/peoplePreferences';
import { useTableColumns, ColumnMap } from '../../hooks/tableColumns';
import { usePaginatedRedVelvetQuery } from '../../hooks/paginatedRedVelvetQuery';
import { convertTokensToPeopleQuery } from '../../utils/attendance/peopleSearch';
import { AmazonSignup, SignupStatus } from '../../utils/signupUtils';
import { publishKatalMetric } from '../katalAnalytics';

export interface SignupSearchTableProps {
  existingSignups: AmazonSignup[];
  pendingSignupAliases: Set<string>;
  onAddToSignupList: (alias: string) => void;
  onRemoveFromSignupList: (alias: string) => void;
  onBulkAddToSignupList: (aliases: string[]) => void;
  isAtCapacity: boolean;
  effectiveRemainingSpots: number;
  metricKeys: { searchPeopleFilterMetricKey: string; addSignupFromSearchMetricKey: string };
}

enum SignupActionType {
  ADD = 'add',
  REMOVE = 'remove',
  NONE = 'none',
}

function getPersonName(person: Person): string {
  return `${person.lastName}, ${person.firstName}`;
}

const personColumnMap: ColumnMap<Person> = {
  name: (item) => getPersonName(item),
  alias: (item) => (
    <Link target="_blank" href={`https://phonetool.amazon.com/users/${item.alias}`}>
      {item.alias}
    </Link>
  ),
  managerLogin: (item) => (
    <Link target="_blank" href={`https://phonetool.amazon.com/users/${item.managerLogin}`}>
      {item.managerLogin}
    </Link>
  ),
  building: (item) => item.building || '',
};

export function SignupSearchTable({
  existingSignups,
  pendingSignupAliases,
  onAddToSignupList,
  onRemoveFromSignupList,
  onBulkAddToSignupList,
  isAtCapacity,
  effectiveRemainingSpots,
  metricKeys,
}: SignupSearchTableProps) {
  const namespaces = ['signup'];
  const { t } = useTranslation(namespaces);
  const tableStrings = useTableStrings(
    'signupSearchTable',
    (item: Person) => getPersonName(item),
    namespaces
  );
  const paginationStrings = usePaginationStrings('signupSearchTable', namespaces);

  const [pageSize, setPageSize] = useState(100);
  const [selectedItems, setSelectedItems] = useState<Person[]>([]);
  const [largestPage, setLargestPage] = useState(1);
  const [filterObj, setFilterObj] = useState<PropertyFilterProps.Query>({
    tokens: [],
    operation: 'or',
  });

  const existingAliases = new Set(
    existingSignups.filter((s) => s.signupStatus !== SignupStatus.CANCEL).map((s) => s.alias)
  );

  const getActionType = (alias: string): SignupActionType => {
    if (existingAliases.has(alias)) return SignupActionType.NONE;
    if (pendingSignupAliases.has(alias)) return SignupActionType.REMOVE;
    return SignupActionType.ADD;
  };

  const searchQuery = convertTokensToPeopleQuery(filterObj);
  const { pageIndex, setPageIndex, queryResult } = usePaginatedRedVelvetQuery({
    redVelvetQueryKey: ['getPeople', { ...searchQuery, pageSize }],
  });
  const people = React.useMemo(
    () =>
      filterObj.tokens.length > 0
        ? (queryResult.data?.people || []).filter((p): p is Person & { alias: string } => !!p.alias)
        : [],
    [filterObj.tokens.length, queryResult.data?.people]
  );

  const activeColumnMap: ColumnMap<Person> = {
    ...personColumnMap,
    actions: (item) => {
      if (!item.alias) return '';
      const alias = item.alias;
      const actionType = getActionType(alias);
      if (actionType === SignupActionType.NONE) return '';
      if (actionType === SignupActionType.REMOVE) {
        return (
          <Button variant="normal" onClick={() => onRemoveFromSignupList(alias)}>
            {t('signupSearchTable.remove')}
          </Button>
        );
      }
      return (
        <Button
          variant="normal"
          disabled={isAtCapacity}
          disabledReason={t('signupSheet.blockedReasons.SHIFT_FULL')}
          onClick={() => {
            publishKatalMetric(metricKeys.addSignupFromSearchMetricKey, alias);
            onAddToSignupList(alias);
          }}
        >
          {t('signupSearchTable.addToSignupList')}
        </Button>
      );
    },
  };

  const columns = useTableColumns(activeColumnMap, 'signupSearchTable', namespaces);

  const isOpenEnded = !queryResult.isLoading && queryResult.data?.page !== undefined;
  const foundItems = isOpenEnded
    ? pageSize * largestPage + '+'
    : pageSize * (pageIndex - 1) + (people.length || 0);

  const addableSelected = selectedItems.filter(
    (item) => item.alias && getActionType(item.alias) === SignupActionType.ADD
  );

  return (
    <Table
      header={
        <Header
          counter={`(${foundItems})`}
          description={t('signupSearchTable.description')}
          variant="h3"
          actions={
            <Button
              variant="primary"
              disabled={
                addableSelected.length === 0 || addableSelected.length > effectiveRemainingSpots
              }
              disabledReason={
                addableSelected.length > effectiveRemainingSpots
                  ? t('signupSheet.blockedReasons.SHIFT_FULL')
                  : undefined
              }
              onClick={() => {
                const aliases = addableSelected
                  .map((item) => item.alias)
                  .filter((a): a is string => !!a);
                aliases.forEach((alias) =>
                  publishKatalMetric(metricKeys.addSignupFromSearchMetricKey, alias)
                );
                onBulkAddToSignupList(aliases);
                setSelectedItems([]);
              }}
            >
              {t('signupSearchTable.addSelectedToList', { count: addableSelected.length })}
            </Button>
          }
        >
          {t('signupSearchTable.title')}
        </Header>
      }
      filter={
        <PeoplePropertyFilter
          query={filterObj}
          onChange={({ detail }) => {
            const metricValue = detail.tokens
              .map((token) => `${token.propertyKey || 'fuzzySearch'}:${token.value}`)
              .join(',');
            publishKatalMetric(metricKeys.searchPeopleFilterMetricKey, metricValue);
            setSelectedItems([]);
            setPageIndex(1);
            setLargestPage(1);
            setFilterObj(detail);
          }}
        />
      }
      pagination={
        <Pagination
          ariaLabels={paginationStrings}
          currentPageIndex={pageIndex}
          onChange={({ detail }) => {
            setLargestPage(Math.max(detail.currentPageIndex, largestPage));
            setPageIndex(detail.currentPageIndex);
          }}
          openEnd={isOpenEnded}
          pagesCount={largestPage}
        />
      }
      preferences={
        <PeoplePreferences
          onConfirm={({ detail }) => setPageSize(detail.pageSize || 100)}
          preferences={{ pageSize }}
        />
      }
      onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
      selectedItems={selectedItems}
      selectionType="multi"
      isItemDisabled={(item) => (!!item.alias && existingAliases.has(item.alias)) || isAtCapacity}
      {...tableStrings}
      loading={queryResult.isLoading}
      empty={
        <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
          <b>
            {queryResult.isError
              ? queryResult.error.name.startsWith('4')
                ? t('signupSearchTable.badRequestMessage')
                : t('signupSearchTable.errorOccurred')
              : t('signupSearchTable.noneFound')}
          </b>
        </Box>
      }
      {...columns}
      items={people}
      trackBy="alias"
      data-testid="signup-search-table"
    />
  );
}
